import { ActivityIndicator, Pressable, StyleSheet, Text } from 'react-native';
import type { PressableProps, TextStyle, ViewStyle } from 'react-native';

import { Icon } from '../icons/Icon';
import type { IconName } from '../icons/iconNames';
import { useTheme } from '../theme/ThemeProvider';
import type { ThemeTokens } from '../theme/tokens';

export type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost';

export interface ButtonProps extends Omit<PressableProps, 'children' | 'style'> {
  title: string;
  variant?: ButtonVariant;
  loading?: boolean;
  icon?: IconName;
}

function palette(theme: ThemeTokens, variant: ButtonVariant): { background: string; text: string; border: string } {
  switch (variant) {
    case 'secondary':
      return { background: theme.colors.surface, text: theme.colors.text, border: theme.colors.border };
    case 'danger':
      return { background: theme.colors.danger, text: theme.colors.onPrimary, border: theme.colors.danger };
    case 'ghost':
      return { background: 'transparent', text: theme.colors.primary, border: 'transparent' };
    case 'primary':
    default:
      return { background: theme.colors.primary, text: theme.colors.onPrimary, border: theme.colors.primary };
  }
}

export function Button({
  title,
  variant = 'primary',
  loading = false,
  icon,
  disabled,
  ...rest
}: ButtonProps) {
  const theme = useTheme();
  const colors = palette(theme, variant);
  const isDisabled = disabled === true || loading;

  const containerStyle: ViewStyle = {
    backgroundColor: colors.background,
    borderColor: colors.border,
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: theme.radius.md,
    paddingVertical: theme.spacing(2.5),
    paddingHorizontal: theme.spacing(4),
    gap: theme.spacing(2),
    opacity: isDisabled ? 0.5 : 1,
  };
  const labelStyle: TextStyle = {
    color: colors.text,
    fontSize: theme.fontSize.md,
    textAlign: 'center',
    ...(theme.fontFamily !== undefined
      ? { fontFamily: theme.fontFamily.semibold }
      : { fontWeight: theme.fontWeight.semibold }),
  };

  return (
    <Pressable
      accessibilityRole="button"
      disabled={isDisabled}
      style={({ pressed }) => [containerStyle, styles.center, pressed && !isDisabled && styles.pressed]}
      {...rest}
    >
      {loading ? (
        <ActivityIndicator color={colors.text} />
      ) : (
        <>
          {icon !== undefined ? <Icon name={icon} size="sm" color={colors.text} /> : null}
          <Text style={labelStyle}>{title}</Text>
        </>
      )}
    </Pressable>
  );
}

const styles = StyleSheet.create({
  center: { alignItems: 'center', justifyContent: 'center', flexDirection: 'row' },
  pressed: { opacity: 0.8 },
});
